import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Megaphone, Trash2, X } from 'lucide-react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { tokenManager, isInCfCooldown, cfCooldownRemainingMs } from '../api/apiClient';

const API_URL = window.__BACKEND_URL__;
const MAX_WS_FAILURES = 4;
const BASE_BACKOFF_MS = 15000; // 15s
const MAX_BACKOFF_MS = 5 * 60 * 1000; // 5 min

// Messenger already has its own notification surfaces:
// - MessagesDropdown
// - Messenger unread badges
// - /messages inbox/request tabs
// - sounds/read receipts
// So Messenger/message records must not appear in the global bell dropdown.
const MESSENGER_NOTIFICATION_CATEGORIES = new Set([
  'message',
  'messages',
  'direct_message',
  'direct_messages',
  'dm',
  'dms',
  'messenger',
  'chat',
  'chats',
  'conversation',
  'conversations',
  'group_message',
  'group_messages',
  'group_chat',
  'group_chats',
  'voice_note',
  'voice_notes',
  'nudge',
  'nudges',
  'message_request',
  'message_requests',
  'conversation_request',
  'conversation_requests',
]);

const MESSENGER_NOTIFICATION_TYPES = new Set([
  'message',
  'messages',
  'new_message',
  'message_created',
  'message_sent',
  'message_received',
  'direct_message',
  'direct_message_received',
  'dm',
  'dm_received',
  'group_message',
  'group_message_received',
  'voice_note',
  'voice_note_received',
  'nudge',
  'nudge_received',
  'message_request',
  'new_message_request',
  'conversation',
  'conversation_created',
  'conversation_updated',
  'messenger',
]);

function normaliseKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
}

function isMessengerNotification(notification) {
  if (!notification) return false;

  const metadata = notification.metadata || {};
  const keysToCheck = [
    notification.category,
    notification.type,
    notification.event_type,
    notification.notification_type,
    notification.kind,
    notification.source,
    metadata.category,
    metadata.type,
    metadata.event_type,
    metadata.notification_type,
    metadata.kind,
    metadata.source,
  ]
    .map(normaliseKey)
    .filter(Boolean);

  if (keysToCheck.some((key) => MESSENGER_NOTIFICATION_CATEGORIES.has(key))) return true;
  if (keysToCheck.some((key) => MESSENGER_NOTIFICATION_TYPES.has(key))) return true;

  // Defensive fallback for older backend records. These clearly belong to
  // Messenger and should not inflate the main notification bell count.
  if (notification.message_id || notification.conversation_id) return true;
  if (metadata.message_id || metadata.conversation_id || metadata.thread_id || metadata.chat_id) return true;

  const link = normaliseKey(notification.link || notification.url || notification.action_url || notification.target_url);
  if (link.startsWith('/messages') || link.includes('/messages/')) return true;

  return keysToCheck.some((key) => (
    key.includes('message')
    || key.includes('messenger')
    || key.includes('conversation')
    || key.includes('nudge')
    || key.includes('voice_note')
  ));
}

function isAdminBroadcastNotification(notification) {
  if (!notification) return false;
  const metadata = notification.metadata || {};
  const keys = [
    notification.type,
    notification.event_type,
    notification.notification_type,
    metadata.type,
    metadata.event_type,
    metadata.source,
  ].map(normaliseKey).filter(Boolean);

  return Boolean(
    metadata.admin_broadcast === true
    || metadata.overlay === true
    || metadata.show_overlay === true
    || keys.includes('admin_broadcast')
    || keys.includes('maintenance_notice')
    || keys.includes('promotion_broadcast')
  );
}

function getAdminBroadcastActionUrl(notification) {
  if (!notification) return null;
  const metadata = notification.metadata || {};
  const value = metadata.action_url
    || metadata.link
    || metadata.url
    || notification.action_url
    || notification.target_url;
  const text = String(value || '').trim();

  // destination_url may be the safe fallback /notifications. That should
  // reopen the popup/list, not create an action button inside the popup.
  if (!text || text === '/notifications') return null;
  return text;
}

function filterGlobalNotifications(list, isAdmin) {
  const safeList = Array.isArray(list) ? list : [];

  // Admin system events keep their existing behaviour. User-facing global
  // notifications are where duplicate DM noise is a problem.
  if (isAdmin) return safeList;

  return safeList.filter((notification) => !isMessengerNotification(notification));
}

function isNotifUnread(notification) {
  // Match NotificationsPage semantics exactly so the header bell badge
  // and the /notifications page tabs count the same thing:
  //   - `read === false` OR `is_read === false`  → unread
  //   - `undefined` on both fields                → treated as read
  // A notification that has been archived is NEVER counted as unread
  // regardless of its read flags (archived rows are hidden from the
  // main list on the page).
  if (!notification) return false;
  if (notification.archived || notification.is_archived) return false;
  return notification.read === false || notification.is_read === false;
}

function countUnread(list) {
  return (Array.isArray(list) ? list : []).filter(isNotifUnread).length;
}

// Generate a human-readable message from admin notification event_type + metadata.
function getAdminNotifMessage(notif) {
  const { event_type, metadata } = notif || {};

  if (event_type === 'user_registered') return `New user registered: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_archived') return `${metadata?.count || 1} user(s) archived`;
  if (event_type === 'user_unarchived') return `User unarchived: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_bulk_deleted') return `${metadata?.count || 1} user(s) deleted`;
  if (event_type === 'user_upgraded') return `User upgraded: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_downgraded') return `User downgraded: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'payment_failed') return `Payment failed for ${metadata?.email || 'a user'}`;
  if (event_type === 'inactivity_archive_run') return `Inactivity archive: ${metadata?.count || 0} users archived`;

  return event_type?.replace(/_/g, ' ') || 'New notification';
}

export function NotificationsDropdown() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.active_role === 'admin' || user?.role === 'admin';

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [pausedCategories, setPausedCategories] = useState([]);
  const [overlayNotification, setOverlayNotification] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const failureCount = useRef(0);
  const wsGaveUp = useRef(false);
  const connectWsRef = useRef(null);
  const toastedIds = useRef(new Set());
  const baselineReady = useRef(false);

  // Labels shown in the paused chip. Messenger categories are intentionally
  // excluded because Messenger owns its own notification surface.
  const CATEGORY_LABELS = {
    project_updates: 'Project activity',
    system_alerts: 'System alerts',
    applications: 'Applications',
    approvals: 'Approvals',
    network: 'Network',
    planner: 'Planner',
  };

  const isInQuietHours = useCallback(() => {
    try {
      if (typeof window === 'undefined') return false;
      if (window.localStorage?.getItem('quietHoursEnabled') !== 'on') return false;

      const start = window.localStorage.getItem('quietHoursStart') || '22:00';
      const end = window.localStorage.getItem('quietHoursEnd') || '08:00';
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;

      return startMin <= endMin
        ? nowMin >= startMin && nowMin < endMin
        : nowMin >= startMin || nowMin < endMin;
    } catch {
      return false;
    }
  }, []);

  const playTone = useCallback(() => {
    try {
      if (typeof window === 'undefined') return;
      const enabled = window.localStorage?.getItem('notificationTone');
      if (enabled === 'off') return;
      if (isInQuietHours()) return;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
      setTimeout(() => ctx.close?.(), 500);
    } catch {
      // Audio may be blocked until first user gesture.
    }
  }, [isInQuietHours]);

  const showDeltaToast = useCallback((n) => {
    if (!n || !n.id) return;
    if (toastedIds.current.has(n.id)) return;
    if (!baselineReady.current) return;

    toastedIds.current.add(n.id);

    if (toastedIds.current.size > 200) {
      const first = toastedIds.current.values().next().value;
      toastedIds.current.delete(first);
    }

    // Standard incoming notifications already surface in the header bell
    // and full Notifications page. Do not create a third Sonner popup.
    // Admin broadcasts remain intentionally interruptive.
    if (!isAdmin && isAdminBroadcastNotification(n)) {
      setOverlayNotification(n);
    }
  }, [isAdmin]);

  const maybeToastPolledDeltas = useCallback((list) => {
    const visibleList = filterGlobalNotifications(list, isAdmin);
    if (!Array.isArray(visibleList)) return;

    if (!baselineReady.current) {
      visibleList.slice(0, 50).forEach((n) => n?.id && toastedIds.current.add(n.id));
      baselineReady.current = true;
      return;
    }

    const unread = visibleList.filter((n) => n && !n.read && !toastedIds.current.has(n.id));
    unread.slice(0, 3).forEach(showDeltaToast);
    unread.slice(3).forEach((n) => n?.id && toastedIds.current.add(n.id));
  }, [isAdmin, showDeltaToast]);

  const fetchPausedCategories = useCallback(async () => {
    if (isAdmin) return;
    if (isInCfCooldown()) return;

    try {
      const { data } = await axios.get(`${API_URL}/api/account/notification-preferences`, { withCredentials: true });
      const inApp = data?.preferences?.in_app || {};
      const paused = Object.entries(inApp)
        .filter(([cat, enabled]) => (
          cat !== 'system_alerts'
          && enabled === false
          && !MESSENGER_NOTIFICATION_CATEGORIES.has(normaliseKey(cat))
        ))
        .map(([cat]) => cat);

      setPausedCategories(paused);
    } catch {
      // Non-fatal.
    }
  }, [isAdmin]);

  const fetchNotifications = useCallback(async () => {
    // If a Cloudflare/origin error happened in the last 60s, skip this
    // poll tick — pounding the origin during a 5xx/WAF storm only
    // amplifies the problem. The interval will re-arm and try again on
    // its next natural tick.
    if (isInCfCooldown()) return;
    try {
      if (isAdmin) {
        const { data } = await axios.get(`${API_URL}/api/admin/notifications?limit=30`, { withCredentials: true });
        const notifs = (data.notifications || []).map((n) => ({
          ...n,
          message: n.message || getAdminNotifMessage(n),
        }));

        setNotifications(notifs);
        // Prefer the server's canonical count (correct even when the
        // caller only asked for 30 rows) but fall back to a local
        // recount using the SAME semantics the /notifications page uses
        // so the badge and the page tabs can never drift.
        if (typeof data?.unread_count === 'number') {
          setUnreadCount(data.unread_count);
        } else {
          setUnreadCount(countUnread(notifs));
        }
        maybeToastPolledDeltas(notifs);
      } else {
        const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
        const notifs = filterGlobalNotifications(data.notifications || [], false);

        setNotifications(notifs);
        setUnreadCount(countUnread(notifs));
        maybeToastPolledDeltas(notifs);
      }
    } catch {
      // Silent fail — polling will retry.
    }
  }, [isAdmin, maybeToastPolledDeltas]);

  const scheduleReconnect = useCallback(() => {
    if (wsGaveUp.current) return;

    if (failureCount.current >= MAX_WS_FAILURES) {
      wsGaveUp.current = true;
      return;
    }

    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);

    // If we just hit a Cloudflare/origin storm via HTTP, also delay the
    // WS reconnect by at least the cooldown remainder. WS handshakes go
    // through the same Cloudflare edge and tripping the WAF more times
    // only digs the hole deeper.
    const backoffDelay = Math.min(
      BASE_BACKOFF_MS * Math.pow(2, Math.max(0, failureCount.current - 1)),
      MAX_BACKOFF_MS,
    );
    const cfDelay = cfCooldownRemainingMs();
    const delay = Math.max(backoffDelay, cfDelay);
    if (cfDelay > 0) {
      // eslint-disable-next-line no-console
      console.info('[ccuk-network] notification-ws-backoff', { delay_ms: delay, cf_cooldown_ms: cfDelay });
    }

    reconnectTimer.current = setTimeout(
      () => connectWsRef.current && connectWsRef.current(),
      delay,
    );
  }, []);

  const connectWs = useCallback(() => {
    if (wsGaveUp.current) return;

    try {
      const token = tokenManager.get();
      if (!token) return;

      const wsBase = API_URL.replace(/^http/, 'ws');
      const url = isAdmin
        ? `${wsBase}/api/admin/notifications/ws?token=${token}`
        : `${wsBase}/api/notifications/ws?token=${token}`;
      const ws = new WebSocket(url);

      ws.onopen = () => {
        failureCount.current = 0;
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);

          if (msg.type === 'connected') {
            if (isAdmin) {
              setUnreadCount(msg.unread_count ?? 0);
            } else {
              fetchNotifications();
            }
            return;
          }

          if (msg.type === 'new_notification') {
            const incoming = msg.notification || {};

            // Do not show, count, toast or tone Messenger events in the
            // global notification bell. Messenger owns those.
            if (!isAdmin && isMessengerNotification(incoming)) return;

            const enriched = isAdmin
              ? { ...incoming, message: incoming.message || getAdminNotifMessage(incoming) }
              : incoming;

            setNotifications((prev) => {
              const next = filterGlobalNotifications([enriched, ...prev], isAdmin).slice(0, 50);
              if (!isAdmin) setUnreadCount(countUnread(next));
              return next;
            });

            if (isAdmin) {
              setUnreadCount(msg.unread_count ?? 0);
            }

            playTone();
            showDeltaToast(enriched);

            // Re-fetch the canonical API shape so fresh WebSocket records
            // immediately gain destination_url, archive/read state and any
            // server-side normalisation instead of staying as a partial row.
            fetchNotifications();
            return;
          }

          if (msg.type === 'read_changed') {
            if (isAdmin) {
              setUnreadCount(msg.unread_count ?? 0);
            } else {
              fetchNotifications();
            }
          }
        } catch {
          // Ignore malformed WS frames.
        }
      };

      ws.onclose = (ev) => {
        if (ev && ev.code === 1008) {
          wsGaveUp.current = true;
          return;
        }

        failureCount.current += 1;
        scheduleReconnect();
      };

      ws.onerror = () => {
        // Let onclose handle reconnect scheduling.
      };

      wsRef.current = ws;
    } catch {
      failureCount.current += 1;
      scheduleReconnect();
    }
  }, [isAdmin, playTone, scheduleReconnect, showDeltaToast, fetchNotifications]);

  useEffect(() => {
    connectWsRef.current = connectWs;
  }, [connectWs]);

  useEffect(() => {
    if (!user) return undefined;

    fetchNotifications();
    fetchPausedCategories();
    connectWs();

    const interval = setInterval(fetchNotifications, 60000);
    const handleRefresh = () => fetchNotifications();
    const handlePrefsRefresh = () => fetchPausedCategories();

    window.addEventListener('admin:stats-refresh', handleRefresh);
    window.addEventListener('admin:notifications-changed', handleRefresh);
    window.addEventListener('notifications:changed', handleRefresh);
    window.addEventListener('notifications:prefs-changed', handlePrefsRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('admin:stats-refresh', handleRefresh);
      window.removeEventListener('admin:notifications-changed', handleRefresh);
      window.removeEventListener('notifications:changed', handleRefresh);
      window.removeEventListener('notifications:prefs-changed', handlePrefsRefresh);

      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);

      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.onopen = null;

        if (wsRef.current.readyState === WebSocket.OPEN) {
          try {
            wsRef.current.close();
          } catch {
            // No-op.
          }
        }

        wsRef.current = null;
      }
    };
  }, [user, fetchNotifications, fetchPausedCategories, connectWs]);

  const markAsRead = async (notificationId) => {
    try {
      let canonicalUnread = null;

      if (isAdmin) {
        const { data } = await axios.post(
          `${API_URL}/api/admin/notifications/mark-read`,
          { notification_ids: [notificationId] },
          { withCredentials: true },
        );

        if (typeof data?.unread_count === 'number') canonicalUnread = data.unread_count;
      } else {
        await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, { withCredentials: true });
      }

      setNotifications((prev) => {
        const next = prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n));
        if (!isAdmin) setUnreadCount(countUnread(next));
        return next;
      });

      if (isAdmin) {
        setUnreadCount((prev) => (canonicalUnread !== null ? canonicalUnread : Math.max(0, prev - 1)));
      }

      window.dispatchEvent(new Event('notifications:changed'));
    } catch {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);

    try {
      if (isAdmin) {
        await axios.post(`${API_URL}/api/admin/notifications/mark-all-read`, {}, { withCredentials: true });
      } else {
        // Only mark visible global notifications read. Messenger records are
        // deliberately excluded so Messenger unread state remains independent.
        const visibleUnreadIds = notifications
          .filter((n) => n && !n.read && !isMessengerNotification(n))
          .map((n) => n.id)
          .filter(Boolean);

        await Promise.all(
          visibleUnreadIds.map((id) => axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true })),
        );
      }

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
      window.dispatchEvent(new Event('admin:notifications-changed'));
      window.dispatchEvent(new Event('notifications:changed'));
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setLoading(false);
    }
  };

  const clearAllAdminNotifications = async () => {
    if (!isAdmin || loading) return;
    const confirmed = window.confirm(
      'Clear all your admin notifications? This only clears your own admin feed and cannot be undone.',
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/admin/notifications?confirm=true`,
        { withCredentials: true },
      );
      setNotifications([]);
      setUnreadCount(0);
      toast.success(
        data?.deleted === 1
          ? '1 admin notification cleared'
          : `${data?.deleted || 0} admin notifications cleared`,
      );
      window.dispatchEvent(new Event('admin:notifications-changed'));
      window.dispatchEvent(new Event('notifications:changed'));
    } catch {
      toast.error('Failed to clear admin notifications');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <>
      <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) fetchNotifications();
      }}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-theme-text hover:text-theme-primary hover:bg-theme-secondary h-10 w-10"
          data-testid="header-notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs border-2 border-theme">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 max-h-[500px] overflow-y-auto bg-theme-surface border-theme z-[110]"
      >
        <div className="px-4 py-3 border-b border-theme sticky top-0 bg-theme-surface z-10">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-theme-text">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                disabled={loading}
                className="text-xs text-theme-primary hover:text-theme-primary-hover"
              >
                Mark all read
              </Button>
            )}
          </div>

          {pausedCategories.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/dashboard?tab=settings');
              }}
              className="mt-2 w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors"
              data-testid="paused-chip"
              title="Click to manage notification channels"
            >
              <span className="inline-flex items-center gap-1.5 min-w-0 text-[11px] font-medium truncate">
                <BellOff className="h-3 w-3 shrink-0" />
                {pausedCategories.length === 1
                  ? `${CATEGORY_LABELS[pausedCategories[0]] || pausedCategories[0]} paused`
                  : `${pausedCategories.length} categories paused`}
              </span>
              <span className="text-[10px] uppercase tracking-wider font-semibold shrink-0">Manage</span>
            </button>
          )}
        </div>

        <div className="divide-y divide-theme">
          {notifications.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Bell className="h-12 w-12 text-theme-muted mx-auto mb-3 opacity-50" />
              <p className="text-theme-muted text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => {
                  // Mark read on open so the unread count is consistent
                  // with the user's intent. Best-effort — the navigation
                  // happens regardless of the read PUT succeeding.
                  if (!notification.read) markAsRead(notification.id);
                  if (!isAdmin && isAdminBroadcastNotification(notification)) {
                    setOpen(false);
                    setOverlayNotification(notification);
                    return;
                  }
                  const dest = notification.destination_url
                    || notification.metadata?.action_url
                    || notification.link
                    || notification.url
                    || notification.action_url
                    || notification.target_url;
                  if (dest) {
                    setOpen(false);
                    navigate(dest);
                  }
                }}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  notification.read
                    ? 'bg-theme-surface hover:bg-theme-secondary'
                    : 'bg-[#002FA7]/10 hover:bg-[#002FA7]/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-[#002FA7] mt-1.5 flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notification.read ? 'text-theme-muted' : 'text-theme-text font-medium'}`}>
                      {notification.message}
                    </p>

                    <p className="text-xs text-theme-muted mt-1">
                      {formatDate(notification.created_at || notification.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="sticky bottom-0 border-t border-theme bg-theme-surface p-3 space-y-1">
          {isAdmin && notifications.length > 0 && (
            <button
              type="button"
              onClick={clearAllAdminNotifications}
              disabled={loading}
              className="w-full rounded-xl px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-500/10 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5"
              data-testid="admin-notif-bell-clear-all"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear all notifications
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-theme-primary hover:bg-theme-secondary transition-colors"
            data-testid="notif-bell-view-all"
          >
            View all notifications
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/dashboard?tab=settings');
            }}
            className="w-full rounded-xl px-4 py-2 text-xs font-medium text-theme-muted hover:text-theme-text hover:bg-theme-secondary transition-colors"
            data-testid="notif-bell-settings"
          >
            Notification settings
          </button>
        </div>
      </DropdownMenuContent>

    </DropdownMenu>

      {overlayNotification && !isAdmin && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center px-4 py-6" role="dialog"
      data-ccuk-mobile-dialog-root="true" aria-modal="true" data-testid="admin-broadcast-overlay">
          <button
            type="button"
            className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[2px]"
            onClick={() => setOverlayNotification(null)}
            aria-label="Dismiss notification overlay backdrop"
          />
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-theme bg-theme-surface shadow-2xl">
            <div className="absolute inset-x-0 top-0 h-1 bg-[#002FA7]" />
            <button
              type="button"
              onClick={() => setOverlayNotification(null)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-theme-muted hover:bg-theme-secondary hover:text-theme-text"
              aria-label="Dismiss notification"
              data-testid="admin-broadcast-overlay-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="p-6 pr-12">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#002FA7]/10 text-[#002FA7]">
                <Megaphone className="h-6 w-6" />
              </div>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#002FA7]">Casting Calls UK update</p>
              <h2 className="text-xl font-bold text-theme-text" data-testid="admin-broadcast-overlay-title">
                {overlayNotification.title || overlayNotification.metadata?.title || 'Platform update'}
              </h2>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-theme-muted" data-testid="admin-broadcast-overlay-message">
                {overlayNotification.message}
              </p>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (overlayNotification?.id && !overlayNotification.read) markAsRead(overlayNotification.id);
                    setOverlayNotification(null);
                  }}
                  className="border-theme text-theme-text"
                  data-testid="admin-broadcast-overlay-dismiss"
                >
                  Dismiss
                </Button>
                {getAdminBroadcastActionUrl(overlayNotification) && (
                  <Button
                    type="button"
                    onClick={() => {
                      if (overlayNotification?.id && !overlayNotification.read) markAsRead(overlayNotification.id);
                      const dest = getAdminBroadcastActionUrl(overlayNotification);
                      setOverlayNotification(null);
                      if (dest) navigate(dest);
                    }}
                    className="bg-[#002FA7] text-white hover:bg-[#0055FF]"
                    data-testid="admin-broadcast-overlay-open"
                  >
                    Open
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
